import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  type Theme,
  Tooltip,
  alpha,
} from '@mui/material';
import { sortTemplatesByDefault } from '@realadvisor/common-types';
import {
  REALADVISOR_PORTAL_ID,
  computePortalReference,
} from '@realadvisor/listings';
import {
  BookOpen,
  CalendarDays,
  ExternalLink,
  Eye,
  FileChartColumn,
  Pencil,
  Share2,
} from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { type ListingQuickActionsFragment } from '@graphql/__generated__/graphql';
import { ListingPortalsButton } from '@modules/_shared/components/listing-overview/ListingPortalsButton';
import { ListingStatusSelect } from '@modules/_shared/components/listing-overview/ListingStatusSelect';
import { TemplateViewerDialog } from '@modules/_shared/components/TemplateViewerDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { canAccessMls } from '@modules/_shared/utils/access';
import { getGlobalTemplateId } from '@modules/_shared/utils/document-url';
import { useLocale } from '@shared/hooks/locale';
import { borderRadius } from '@shared/theme/StyleProvider';
import { toGlobalId } from '@shared/utils/global-id';
import { isRATenantId } from '@shared/utils/tenant';

const GET_LISTING_QUICK_ACTIONS_DATA = gql(/* GraphQL */ `
  query GetListingQuickActionsData {
    seller_report_templates: doc_templates(
      limit: 100
      where: { type: { _eq: seller_report }, active: { _eq: true } }
      order_by: { name: asc }
    ) {
      id
      name
      active
      ...DocTemplateDefaultLinks
    }
    brochure_templates: doc_templates(
      limit: 100
      where: { type: { _eq: lot_brochure }, active: { _eq: true } }
      order_by: { name: asc }
    ) {
      id
      name
      active
      ...DocTemplateDefaultLinks
    }
    lots_status_enum {
      value
    }
  }
`);

export const LISTING_QUICK_ACTIONS_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingQuickActions on lots {
    id
    offer_type
    shared_on_mls_at
    ...ListingStatusSelect
    ...PortalListingButton
    broker_id
    broker {
      id
      teams_users {
        team_id
      }
    }
    sellers(order_by: { seller: { created_at: asc } }) {
      user_id
      seller {
        id
      }
    }
    created_by_user {
      id
    }
    lot_reference_aliases(
      order_by: { reference_alias_decoded: desc }
      limit: 1
    ) {
      reference_alias_decoded
    }
    portal_listings {
      portal {
        increment
      }
    }
  }
`);

const GET_AGGREGATOR_LISTING_URL = gql(/* GraphQL */ `
  query GetAggregatorListingUrl($originalId: String!) {
    online_listings(
      where: {
        portal: { _eq: "realadvisor" }
        original_id: { _eq: $originalId }
        removed: { _eq: false }
      }
      limit: 1
    ) {
      url
    }
  }
`);

interface ListingQuickActionsProps {
  listingData?: ListingQuickActionsFragment | null;
  readOnly?: boolean;
}

const toDocTemplateGlobalId = <T extends { id: string | null | undefined }>(
  template: T,
): Omit<T, 'id'> & { id: string } => ({
  ...template,
  id: template.id != null ? getGlobalTemplateId(template.id) : '',
});

const getCalendarUrl = (
  listingId: string | null | undefined,
  useLegacyLinks: boolean,
) => {
  const lotId =
    useLegacyLinks && listingId != null
      ? toGlobalId('Lot', listingId)
      : listingId;

  return `/calendar?activityType_in=visit&activityStatus_in=done&activityStatus_in=pending&lotId_in=${
    lotId ?? ''
  }`;
};

type QuickActionsMenu = {
  editorHref?: string;
  templates: { id: string; name: string }[];
  previewLabel: string;
};

const quickActionsShellSx = (theme: Theme) => ({
  borderRadius: '10px',
  mb: { xs: 1, sm: 2 },
  transition: 'all 0.15s ease',
  '&:hover': {
    borderColor: (theme.vars ?? theme).palette.grey[300],
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  [theme.breakpoints.down('md')]: {
    borderRadius: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
  },
});

const QuickActionButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  tooltip?: string;
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}> = ({ icon, label, tooltip, href, onClick }) => {
  const button = (
    <ButtonBase
      focusRipple
      {...(href != null
        ? {
            component: 'a' as const,
            href,
            target: '_blank',
            rel: 'noopener noreferrer',
          }
        : {})}
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        minWidth: 64,
        px: 1,
        py: 0.75,
        borderRadius: '8px',
        color: 'text.secondary',
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: 'grey.100',
          color: 'text.primary',
        },
      }}
    >
      {icon}
      <Box
        component="span"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
    </ButtonBase>
  );

  return tooltip != null ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};

const MlsStatusIndicator: React.FC = () => {
  const { t } = useLocale();

  return (
    <Tooltip title={t('Listing shared on the MLS')}>
      <Stack
        direction="row"
        sx={theme => ({
          alignItems: 'center',
          gap: 0.75,
          height: 32,
          px: 1.5,
          borderRadius: borderRadius.medium,
          cursor: 'default',
          bgcolor: alpha(theme.palette.success.main, 0.12),
          color: theme.palette.success.dark,
        })}
      >
        <Share2 size={14} />
        <Box
          component="span"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {t('MLS')}
        </Box>
      </Stack>
    </Tooltip>
  );
};

const QuickActionSkeleton: React.FC = () => (
  <Stack sx={{ alignItems: 'center', gap: 0.5, minWidth: 64, py: 0.75 }}>
    <Skeleton variant="circular" width={18} height={18} />
    <Skeleton variant="text" width={44} sx={{ fontSize: '0.6875rem' }} />
  </Stack>
);

export const ListingQuickActionsSkeleton: React.FC = () => (
  <Paper variant="outlined" sx={quickActionsShellSx}>
    <Stack
      sx={{
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        flexWrap: { sm: 'wrap' },
        gap: 1,
        px: 2,
        py: 1,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Skeleton width={100} height={32} variant="rounded" />
        <Skeleton width={110} height={32} variant="rounded" />
      </Stack>
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
          ml: { sm: 'auto' },
          justifyContent: { xs: 'space-around', sm: 'flex-end' },
        }}
      >
        <QuickActionSkeleton />
        <QuickActionSkeleton />
        <QuickActionSkeleton />
      </Stack>
    </Stack>
  </Paper>
);

export const ListingQuickActions: React.FC<ListingQuickActionsProps> = ({
  listingData,
  readOnly = false,
}) => {
  const { me } = useAppData();
  const { data } = useQuery(GET_LISTING_QUICK_ACTIONS_DATA);
  const { t, countryCode } = useLocale();

  const isMlsEnabled = canAccessMls(me, countryCode);

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menu, setMenu] = useState<QuickActionsMenu | null>(null);

  const tenantId = me?.tenant_id;
  const teamId = me?.default_team?.id;

  const calendarUrl = getCalendarUrl(listingData?.id, true);

  const sellerReportTemplates = sortTemplatesByDefault(
    data?.seller_report_templates.map(toDocTemplateGlobalId) ?? [],
    { tenantId, teamId },
  );

  const brochureTemplates = sortTemplatesByDefault(
    data?.brochure_templates.map(toDocTemplateGlobalId) ?? [],
    { tenantId, teamId },
  );

  const liveRAPortalListing = listingData?.portal_listings.find(
    pl => pl.live && pl.portal_id === REALADVISOR_PORTAL_ID,
  );

  const showListingLink = useMemo(
    () =>
      isRATenantId(me?.tenant?.id ?? '') &&
      liveRAPortalListing != null &&
      [
        'available',
        'available_coming_soon',
        'available_off_market',
        'reserved',
      ].includes(listingData?.status ?? ''),
    [listingData?.status, me?.tenant?.id, liveRAPortalListing],
  );

  const portalIncrement = liveRAPortalListing?.portal.increment;
  const aggregatorOriginalId =
    portalIncrement != null
      ? computePortalReference(
          { lot_reference_aliases: listingData?.lot_reference_aliases ?? [] },
          portalIncrement,
        )
      : null;

  const { data: aggregatorListingData, loading: aggregatorLoading } = useQuery(
    GET_AGGREGATOR_LISTING_URL,
    {
      context: { clientName: 'scrapers' },
      variables: { originalId: aggregatorOriginalId ?? '' },
      skip: !showListingLink || aggregatorOriginalId == null,
    },
  );

  const listingUrl = aggregatorListingData?.online_listings[0]?.url;

  const closeMenu = () => setMenuAnchor(null);

  const openPreview = (id: string) => {
    closeMenu();
    setTemplateId(id);
  };

  const openEditor = (href: string) => {
    closeMenu();
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const handleSellerReportClick = (event: React.MouseEvent<HTMLElement>) => {
    if (sellerReportTemplates.length === 1) {
      openPreview(sellerReportTemplates[0]!.id);
      return;
    }
    setMenu({
      templates: sellerReportTemplates.map(template => ({
        id: template.id,
        name: template.name ?? '',
      })),
      previewLabel: t('Preview'),
    });
    setMenuAnchor(event.currentTarget);
  };

  const handleBrochureClick = (event: React.MouseEvent<HTMLElement>) => {
    if (listingData == null) {
      return;
    }
    const editorHref = readOnly
      ? undefined
      : `/brochure-reports/${listingData.id}/page-cover`;

    if (editorHref != null && brochureTemplates.length === 0) {
      openEditor(editorHref);
      return;
    }
    if (editorHref == null && brochureTemplates.length === 1) {
      openPreview(brochureTemplates[0]!.id);
      return;
    }

    setMenu({
      editorHref,
      templates: brochureTemplates.map(template => ({
        id: template.id,
        name: template.name ?? '',
      })),
      previewLabel: t('Preview brochure'),
    });
    setMenuAnchor(event.currentTarget);
  };

  return (
    <>
      <Paper variant="outlined" sx={quickActionsShellSx}>
        <Stack
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: { sm: 'wrap' },
            gap: 1,
            px: 2,
            py: 1,
          }}
        >
          <Stack
            direction="row"
            sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
          >
            <ListingPortalsButton
              btnSize="small"
              listing={listingData}
              readOnly={readOnly}
            />

            <ListingStatusSelect
              listing={listingData}
              allStatuses={data?.lots_status_enum ?? []}
              disabled={readOnly}
            />

            {listingData != null &&
              isMlsEnabled &&
              listingData.shared_on_mls_at != null && <MlsStatusIndicator />}
          </Stack>

          <Divider sx={{ display: { xs: 'block', sm: 'none' }, mx: -2 }} />

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, my: 0.5, display: { xs: 'none', sm: 'block' } }}
          />

          <Stack
            direction="row"
            sx={{
              gap: 0.5,
              ml: { sm: 'auto' },
              '& > *': { flex: { xs: 1, sm: '0 0 auto' } },
            }}
          >
            {showListingLink && aggregatorLoading && <QuickActionSkeleton />}
            {showListingLink && !aggregatorLoading && listingUrl != null && (
              <QuickActionButton
                icon={<ExternalLink size={18} />}
                label={t('Listing')}
                tooltip={listingUrl}
                href={listingUrl}
              />
            )}

            {listingData != null && sellerReportTemplates.length > 0 && (
              <QuickActionButton
                icon={<FileChartColumn size={18} />}
                label={
                  listingData.offer_type === 'rent'
                    ? t('Lessor Report')
                    : t('Seller Report')
                }
                onClick={handleSellerReportClick}
              />
            )}

            {listingData != null &&
              (brochureTemplates.length > 0 || !readOnly) && (
                <QuickActionButton
                  icon={<BookOpen size={18} />}
                  label={t('Brochure')}
                  onClick={handleBrochureClick}
                />
              )}

            <QuickActionButton
              icon={<CalendarDays size={18} />}
              label={t('calendar')}
              tooltip={t('visitsCalendar')}
              href={calendarUrl}
            />
          </Stack>
        </Stack>
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { mt: 0.5, minWidth: 200 } },
          list: { dense: true },
        }}
      >
        {menu?.editorHref != null && (
          <MenuItem onClick={() => openEditor(menu.editorHref!)}>
            <ListItemIcon>
              <Pencil size={16} />
            </ListItemIcon>
            <ListItemText>{t('Open brochure editor')}</ListItemText>
          </MenuItem>
        )}
        {menu?.editorHref != null && menu.templates.length > 0 && (
          <Divider sx={{ my: 0.5 }} />
        )}
        {menu != null && menu.templates.length > 0 && (
          <ListSubheader
            disableSticky
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
              lineHeight: '28px',
              px: 1.5,
            }}
          >
            {menu.previewLabel}
          </ListSubheader>
        )}
        {menu?.templates.map(template => (
          <MenuItem key={template.id} onClick={() => openPreview(template.id)}>
            <ListItemIcon>
              <Eye size={16} />
            </ListItemIcon>
            <ListItemText>{template.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {listingData != null && (
        <TemplateViewerDialog
          templateId={templateId ?? ''}
          documentId={toGlobalId('Lot', listingData.id)}
          open={templateId != null}
          onClose={() => setTemplateId(null)}
        />
      )}
    </>
  );
};
